<template>
  <b-modal
    v-model="showModal"
    :title="`REGULARIZAR DÍAS LIBRES: ${conductor.personal_name || ''}`"
    modal-class="modal-primary"
    size="lg"
    hide-footer
    no-close-on-backdrop
    scrollable
    @hidden="$emit('hidden')"
  >
    <b-alert show variant="warning" class="p-1">
      <feather-icon icon="AlertTriangleIcon" class="mr-25" />
      Un ajuste positivo le suma días al saldo del conductor y uno negativo se
      los descuenta. Queda registrado con tu usuario y se puede anular.
    </b-alert>

    <validation-observer ref="form">
      <b-row>
        <b-col md="4">
          <validation-provider v-slot="{ errors }" rules="required">
            <b-form-group label="Periodo">
              <b-form-input
                v-model="form.periodo"
                type="month"
                :class="{ 'border-danger': errors[0] }"
              />
            </b-form-group>
          </validation-provider>
        </b-col>

        <b-col md="4">
          <validation-provider v-slot="{ errors }" rules="required">
            <b-form-group label="Días (+ suma / - resta)">
              <b-form-input
                v-model.number="form.cantidad"
                type="number"
                step="0.5"
                :class="{ 'border-danger': errors[0] }"
              />
            </b-form-group>
          </validation-provider>
        </b-col>

        <b-col md="4">
          <b-form-group label="Saldo actual">
            <h4 class="mt-50">
              <b-badge :variant="saldoResultante < 0 ? 'danger' : 'success'">
                {{ saldoActual }} → {{ saldoResultante }}
              </b-badge>
            </h4>
          </b-form-group>
        </b-col>

        <b-col md="12">
          <validation-provider v-slot="{ errors }" rules="required">
            <b-form-group label="Motivo">
              <b-form-select
                v-model="form.motivo"
                :options="motivos"
                :class="{ 'border-danger': errors[0] }"
              />
            </b-form-group>
          </validation-provider>
        </b-col>

        <b-col md="12">
          <b-form-group label="Sustento (opcional)">
            <b-form-textarea
              v-model="form.sustento"
              rows="2"
              placeholder="Detalle, número de documento, acuerdo con el conductor..."
            />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-end mb-2">
        <b-button variant="outline-danger" @click="showModal = false">
          Cancelar
        </b-button>
        <b-button variant="primary" class="ml-1" @click="guardar">
          Registrar ajuste
        </b-button>
      </div>
    </validation-observer>

    <h6 class="font-weight-bolder mb-1">Ajustes registrados</h6>
    <b-table
      small
      responsive
      sticky-header="35vh"
      :items="ajustes"
      :fields="fieldsAjustes"
      show-empty
      empty-text="Sin ajustes registrados"
      :busy="isBusy"
    >
      <template #cell(cantidad)="data">
        <b-badge :variant="data.value < 0 ? 'light-danger' : 'light-success'">
          {{ data.value > 0 ? "+" : "" }}{{ data.value }}
        </b-badge>
      </template>
      <template #cell(tipo)="data">
        <b-badge variant="light-secondary">
          {{ data.value === "saldo_inicial" ? "SALDO INICIAL" : "REGULARIZACIÓN" }}
        </b-badge>
      </template>
      <template #cell(created_at)="data">
        <span>{{ data.value | myGlobalDay }}</span>
      </template>
      <template #cell(acciones)="data">
        <feather-icon
          icon="TrashIcon"
          size="16"
          class="cursor-pointer text-danger"
          v-b-tooltip.hover.left="'Anular ajuste'"
          @click="anular(data.item)"
        />
      </template>
    </b-table>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import DiasLibresReportService from "@/views/monitoring/views/dias-libres/services/dias-libres-report.service.js";

export default {
  props: {
    conductor: {
      type: Object,
      required: true,
    },
    periodo: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showModal: true,
      isBusy: false,
      ajustes: [],
      form: {
        periodo: this.periodo,
        cantidad: null,
        motivo: null,
        sustento: null,
      },
      motivos: [
        { value: null, text: "Seleccione un motivo" },
        {
          value: "Días libres gozados no registrados en el tareo",
          text: "Días libres gozados no registrados en el tareo",
        },
        {
          value: "Días libres registrados por error en el tareo",
          text: "Días libres registrados por error en el tareo",
        },
        {
          value: "Acuerdo con el conductor",
          text: "Acuerdo con el conductor",
        },
        {
          value: "Corrección de saldo inicial",
          text: "Corrección de saldo inicial",
        },
        { value: "Otro", text: "Otro (detallar en el sustento)" },
      ],
      fieldsAjustes: [
        { key: "periodo", label: "Periodo" },
        { key: "cantidad", label: "Días", class: "text-center" },
        { key: "tipo", label: "Tipo", class: "text-center" },
        { key: "motivo", label: "Motivo" },
        { key: "registrado_por_nombre", label: "Registrado por" },
        { key: "created_at", label: "Fecha" },
        { key: "acciones", label: "", class: "text-center" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
    saldoActual() {
      return Number(this.conductor.saldo_actual || 0);
    },
    saldoResultante() {
      return Number(
        (this.saldoActual + Number(this.form.cantidad || 0)).toFixed(2)
      );
    },
  },
  created() {
    this.getAjustes();
  },
  methods: {
    async getAjustes() {
      try {
        this.isBusy = true;
        const { data } = await DiasLibresReportService.getAjustes({
          personal_id: this.conductor.personal_id,
        });
        this.ajustes = data;
      } catch (error) {
        console.log(error);
      } finally {
        this.isBusy = false;
      }
    },

    async guardar() {
      const valido = await this.$refs.form.validate();
      if (!valido) return;

      const { isConfirmed } = await this.showConfirmSwal(
        "¿Registrar el ajuste?",
        `El saldo de ${this.conductor.personal_name} pasará de ${this.saldoActual} a ${this.saldoResultante} días.`
      );
      if (!isConfirmed) return;

      try {
        this.addPreloader();
        const { data } = await DiasLibresReportService.registrarAjuste({
          personal_id: this.conductor.personal_id,
          periodo: this.form.periodo,
          cantidad: this.form.cantidad,
          motivo: this.form.motivo,
          sustento: this.form.sustento,
          tipo: "regularizacion",
          id_usuario: this.currentUser.id,
        });
        this.showToastSuccess(data.message);
        this.$emit("saved");
      } catch (error) {
        this.showToastError(
          error.response?.data?.message || "No se pudo registrar el ajuste"
        );
      } finally {
        this.removePreloader();
      }
    },

    async anular(item) {
      const { isConfirmed } = await this.showConfirmSwal(
        "¿Anular este ajuste?",
        "El saldo del conductor se recalculará sin considerarlo."
      );
      if (!isConfirmed) return;

      try {
        this.addPreloader();
        await DiasLibresReportService.anularAjuste({ id: item.id });
        this.showToastSuccess("Ajuste anulado");
        await this.getAjustes();
        // Refresca la grilla sin cerrar el modal, para poder seguir corrigiendo
        this.$emit("refresh");
      } catch (error) {
        this.showToastError("No se pudo anular el ajuste");
      } finally {
        this.removePreloader();
      }
    },
  },
};
</script>
